<template>
  <div class="flex flex-col justify-between">
    <el-form ref="formRef" label-position="top" :model="form" size="large">
      <div class="space-y-4">
        <div>
          <el-form-item label="角色名称" prop="name" :rules="[{ required: true, message: '请填写角色名称', trigger: 'blur' }]">
            <el-input v-model="form.name" placeholder="请填写角色名称"></el-input>
          </el-form-item>
        </div>
        <div>
          <el-form-item label="权限设置" prop="permissions">
            <el-checkbox-group v-if="permissions.length > 0" v-model="form.permissions"
              @change="handlePermissionChange" class="flex flex-col">
              <template v-for="level1 in permissions" :key="level1.id">
                <el-checkbox disabled class="font-bold" :label="level1.id">{{ level1.name }}</el-checkbox>
                <template v-if="level1.children?.length">
                  <div class="ml-6">
                    <template v-for="level2 in level1.children" :key="level2.id">
                      <el-checkbox :label="level2.id">{{ level2.name }}</el-checkbox>
                      <template v-if="level2.children?.length">
                        <div class="ml-6 flex flex-wrap gap-4">
                          <el-checkbox v-for="level3 in level2.children" :key="level3.id" :label="level3.id">
                            {{ level3.name }}
                          </el-checkbox>
                        </div>
                      </template>
                    </template>
                  </div>
                </template>
              </template>
            </el-checkbox-group>
          </el-form-item>
        </div>
      </div>
    </el-form>
  </div>
</template>

<script setup>
// 在脚本末尾添加 Message 兼容对象
const Message = {
  error: (message) => ElMessage.error(message)
}
const props = defineProps({
  target: {
    type: Object,
    default: () => ({}),
  },
  options: {
    type: Object,
    default: () => ({
      permissions: [],
    }),
  },
})

const emit = defineEmits(['close'])
const formRef = ref(null)
const loading = ref(false)
const permissions = computed(() => {
  const perms = props.options?.permissions || []
  return perms
})
const form = ref(props.target ? { ...props.target } : {
  id: '',
  name: '',
  permissions: [],
})

// 构建权限树的映射关系
const permissionParentMap = computed(() => {
  const map = new Map()

  const buildMap = (items, parent = null) => {
    items.forEach(item => {
      if (parent) {
        map.set(item.id, parent)
      }
      if (item.children?.length) {
        buildMap(item.children, item.id)
      }
    })
  }

  buildMap(permissions.value)
  return map
})

// 构建子节点映射关系
const permissionChildrenMap = computed(() => {
  const map = new Map()

  const buildMap = (items) => {
    items.forEach(item => {
      if (item.children?.length) {
        const children = []
        const getAllChildren = (nodes) => {
          nodes.forEach(node => {
            children.push(node.id)
            if (node.children?.length) {
              getAllChildren(node.children)
            }
          })
        }
        getAllChildren(item.children)
        map.set(item.id, children)
        buildMap(item.children)
      }
    })
  }

  buildMap(permissions.value)
  return map
})

// 处理权限变更
const handlePermissionChange = (values) => {
  const oldPermissions = new Set(form.value.permissions)
  const newPermissions = new Set(values)

  // 找出被取消和新增的权限
  const canceledPermissions = [...oldPermissions].filter(p => !newPermissions.has(p))
  const addedPermissions = [...newPermissions].filter(p => !oldPermissions.has(p))

  // 处理取消的权限：取消所有子节点
  canceledPermissions.forEach(permission => {
    newPermissions.delete(permission)
    const children = permissionChildrenMap.value.get(permission)
    if (children) {
      children.forEach(child => newPermissions.delete(child))
    }
  })

  // 处理新增的权限：添加所有父节点
  addedPermissions.forEach(permission => {
    newPermissions.add(permission)
    let current = permission
    while (permissionParentMap.value.has(current)) {
      const parent = permissionParentMap.value.get(current)
      newPermissions.add(parent)
      current = parent
    }
  })

  form.value.permissions = Array.from(newPermissions)
}

const submit = async () => {
  try {
    loading.value = true
    await formRef.value?.validate()
    
    await router.post(routes.upsert_admin_sys_roles(), form.value, {
      onSuccess: () => { emit('close') },
      onFinish: () => {
        loading.value = false
      }
    })
  } catch (error) {
    ElMessage.error(error?.message || '表单验证失败')
    loading.value = false
  }
}

const close = () => { emit('close') }

// 监听 target 变化，更新表单数据
watchEffect(() => {
  if (props.target) form.value = { ...props.target }
})

defineExpose({ submit })
</script>
